import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Input, Select, Button, Space, message, Table, Tag, Progress, Alert, Tooltip, Upload, Popconfirm, Segmented } from 'antd';
import { DownloadOutlined, FolderOpenOutlined, YoutubeOutlined, UploadOutlined, CheckCircleOutlined, DeleteOutlined, VideoCameraOutlined, CustomerServiceOutlined, PictureOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') : '';

// Three independent sections — pick one, choose its quality, and download.
const VIDEO_QUALITY_OPTIONS = [
  { value: 'best', label: 'Best available' },
  { value: '2160', label: '4K (2160p)' },
  { value: '1440', label: '2K (1440p)' },
  { value: '1080', label: 'Full HD (1080p)' },
  { value: '720', label: 'HD (720p)' },
];
const AUDIO_QUALITY_OPTIONS = [
  { value: 'mp3', label: 'MP3 (most compatible)' },
  { value: 'm4a', label: 'M4A (high quality)' },
];
const MAX_LINKS = 12;
const CONCURRENCY = 2; // download up to 2 at once (rest queue) — VPS is 1-core, keep it smooth
const POLL_MS = 1500;
const fsApiSupported = typeof window !== 'undefined' && 'showDirectoryPicker' in window;

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// "3m 22s" / "45s"
const fmtElapsed = (ms) => {
  if (!ms || ms < 0) return '0s';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};

export default function VideoDownloader({ user }) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState('video'); // video | audio | thumbnail
  const [videoQuality, setVideoQuality] = useState('best');
  const [audioQuality, setAudioQuality] = useState('mp3');
  const quality = mode === 'video' ? videoQuality : mode === 'audio' ? audioQuality : 'thumbnail';
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [dirHandle, setDirHandle] = useState(null);
  const [dirName, setDirName] = useState('');
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
  const [cookieInfo, setCookieInfo] = useState({ present: false, updated_at: null });
  const [uploadingCookies, setUploadingCookies] = useState(false);
  const [, setTick] = useState(0); // re-render every second so elapsed timers update live

  // Tick while any download is running so elapsed time keeps updating.
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const loadCookieStatus = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/downloader/cookies');
      setCookieInfo(res.data);
    } catch { /* ignore */ }
  }, [isAdmin]);

  useEffect(() => { loadCookieStatus(); }, [loadCookieStatus]);

  const uploadCookies = async (file) => {
    setUploadingCookies(true);
    try {
      const content = await file.text();
      await api.post('/downloader/cookies', { content });
      message.success('YouTube cookies saved');
      loadCookieStatus();
    } catch (err) {
      message.error(err.response?.data?.error || 'Cookies upload failed');
    } finally {
      setUploadingCookies(false);
    }
    return false; // prevent antd auto-upload
  };

  const removeCookies = async () => {
    try {
      await api.delete('/downloader/cookies');
      message.success('Cookies removed');
      loadCookieStatus();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to remove cookies');
    }
  };

  const parseLinks = () =>
    text.split(/[\s,]+/).map(s => s.trim()).filter(s => /^https?:\/\//i.test(s));

  const pickFolder = async () => {
    try {
      const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
      setDirHandle(handle);
      setDirName(handle.name);
      message.success(`Files will be saved to folder: ${handle.name}`);
    } catch (err) { /* user cancelled */ }
  };

  const filenameFromHeader = (header, fallback) => {
    if (!header) return fallback;
    const m = /filename="?([^"]+)"?/i.exec(header);
    return m ? m[1] : fallback;
  };

  // Save the finished file (from the server job) into the chosen folder or browser Downloads.
  const saveJobFile = async (jobId, fallbackName) => {
    const token = localStorage.getItem('token');
    let res;
    try {
      res = await fetch(`${API_BASE}/api/downloader/file?job_id=${jobId}&token=${token}`);
    } catch {
      throw new Error('Could not connect to the server while fetching the file');
    }
    if (!res.ok) throw new Error(`File download failed (HTTP ${res.status})`);
    const fname = filenameFromHeader(res.headers.get('Content-Disposition'), fallbackName);
    const blob = await res.blob();
    if (dirHandle) {
      const fileHandle = await dirHandle.getFileHandle(fname, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
    } else {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    }
    return fname;
  };

  // Background-job flow: start -> poll status (live % / speed / ETA) -> fetch finished file.
  const downloadOne = async (url, idx) => {
    const patch = (p) => setRows(r => r.map((x, i) => i === idx ? { ...x, ...p } : x));
    patch({ status: 'starting', percent: 0, startedAt: Date.now(), speed: '', eta: '' });
    try {
      const startRes = await api.post('/downloader/start', { url, quality });
      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error('Could not start the job');
      patch({ jobId, status: 'downloading' });

      let failPolls = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
        let s;
        try {
          // eslint-disable-next-line no-await-in-loop
          s = (await api.get('/downloader/status', { params: { job_id: jobId } })).data;
          failPolls = 0;
        } catch (e) {
          failPolls += 1;
          if (failPolls >= 4) throw new Error('Status check kept failing (server busy?)');
          continue;
        }
        if (s.status === 'downloading') {
          patch({ percent: Math.round(s.percent || 0), speed: s.speed || '', eta: s.eta || '' });
        } else if (s.status === 'done') {
          patch({ percent: 100, status: 'saving', file: s.filename });
          // eslint-disable-next-line no-await-in-loop
          const fname = await saveJobFile(jobId, s.filename || `video_${idx + 1}`);
          patch({ status: 'done', file: fname, eta: '' });
          break;
        } else if (s.status === 'error') {
          throw new Error(s.error || 'Download failed');
        }
      }
    } catch (err) {
      patch({ status: 'error', error: err.response?.data?.error || err.message });
    }
  };

  const startDownload = async () => {
    const links = parseLinks();
    if (links.length === 0) { message.warning('Paste at least one valid link'); return; }
    if (links.length > MAX_LINKS) { message.warning(`Maximum ${MAX_LINKS} links at a time`); return; }
    setRows(links.map(url => ({ url, status: 'queued', percent: 0 })));
    setRunning(true);
    // Concurrency-limited pool: up to CONCURRENCY downloads run together, rest queue.
    let next = 0;
    const worker = async () => {
      while (next < links.length) {
        const i = next;
        next += 1;
        // eslint-disable-next-line no-await-in-loop
        await downloadOne(links[i], i);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, links.length) }, () => worker())
    );
    setRunning(false);
    message.success('All downloads complete (any errors are shown in the table)');
  };

  const statusTag = (s) => {
    const map = {
      queued: <Tag>Queued</Tag>,
      starting: <Tag color="processing">Starting…</Tag>,
      downloading: <Tag color="processing">Downloading…</Tag>,
      saving: <Tag color="processing">Saving…</Tag>,
      done: <Tag color="success">Saved</Tag>,
      error: <Tag color="error">Failed</Tag>,
    };
    return map[s] || <Tag>{s}</Tag>;
  };

  const renderProgress = (r) => {
    if (r.status === 'error') return <Text type="danger">{r.error}</Text>;
    if (r.status === 'done') return <Text type="secondary">{r.file}</Text>;
    if (r.status === 'queued') return <Text type="secondary">Waiting…</Text>;
    if (r.status === 'saving') return <Progress percent={100} size="small" status="active" />;
    const pct = Math.round(r.percent || 0);
    const meta = [r.speed, r.eta && r.eta !== 'Unknown' ? `ETA ${r.eta}` : '']
      .filter(Boolean).join(' · ');
    return (
      <div>
        <Progress percent={pct} size="small" status="active" />
        {meta && <Text type="secondary" style={{ fontSize: 12 }}>{meta}</Text>}
      </div>
    );
  };

  const renderTime = (r) => {
    if (!r.startedAt) return '—';
    return <Text type="secondary" style={{ fontSize: 12 }}>{fmtElapsed(Date.now() - r.startedAt)}</Text>;
  };

  const columns = [
    { title: '#', key: 'n', width: 44, render: (_, __, i) => i + 1 },
    { title: 'Link', dataIndex: 'url', key: 'url', ellipsis: true, render: (u) => <a href={u} target="_blank" rel="noreferrer">{u}</a> },
    { title: 'Status', key: 'status', width: 130, render: (_, r) => statusTag(r.status) },
    { title: 'Progress', key: 'progress', width: 220, render: (_, r) => renderProgress(r) },
    { title: 'Time', key: 'time', width: 80, render: (_, r) => renderTime(r) },
  ];

  const total = rows.length;
  const done = rows.filter(r => r.status === 'done').length;
  const failed = rows.filter(r => r.status === 'error').length;

  return (
    <div>
      <Title level={3} style={{ marginBottom: 16 }}><YoutubeOutlined style={{ color: '#ff0000' }} /> Video Downloader</Title>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Download audio/video from YouTube in full quality (up to 4K)"
        description={
          <span>
            Paste links below (one per line, or separated by space/comma) — max {MAX_LINKS} at a time.
            Choose a quality and click "Download". {fsApiSupported
              ? 'Files will be saved to your chosen folder (choose a folder first).'
              : 'Files will be saved to your browser\'s Downloads folder.'}
            {' '}Use only for your own / allowed content.
          </span>
        }
      />

      {isAdmin && (
        <Card
          size="small"
          style={{ borderRadius: 12, marginBottom: 16, borderColor: cookieInfo.present ? '#52c41a' : '#faad14' }}
          title={<span><YoutubeOutlined style={{ color: '#ff0000' }} /> YouTube Login (cookies) — bot-check bypass</span>}
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Server/VPS se download par YouTube "Sign in to confirm you're not a bot" maangta hai. Ek baar apne
              logged-in YouTube account ki <b>cookies.txt</b> upload karo (browser extension "Get cookies.txt LOCALLY"
              se youtube.com khol ke export karo) — phir saare downloads us account se authenticate ho ke chalenge.
            </Text>
            <Space wrap>
              {cookieInfo.present
                ? <Tag color="success" icon={<CheckCircleOutlined />}>Cookies set{cookieInfo.updated_at ? ` (updated ${cookieInfo.updated_at})` : ''}</Tag>
                : <Tag color="warning">Cookies not set</Tag>}
              <Upload accept=".txt" showUploadList={false} beforeUpload={uploadCookies}>
                <Button icon={<UploadOutlined />} loading={uploadingCookies}>Upload cookies.txt</Button>
              </Upload>
              {cookieInfo.present && (
                <Popconfirm title="Remove cookies?" onConfirm={removeCookies} okText="Yes" cancelText="No">
                  <Button danger icon={<DeleteOutlined />}>Remove</Button>
                </Popconfirm>
              )}
            </Space>
          </Space>
        </Card>
      )}

      <Card style={{ borderRadius: 12, marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <TextArea
            rows={6}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={"https://www.youtube.com/watch?v=...\nhttps://youtu.be/...\nhttps://www.youtube.com/watch?v=..."}
          />
          {/* 3 alag-alag sections — Video / Audio / Thumbnail — jo select karo wahi download hoga. */}
          <Segmented
            value={mode}
            onChange={setMode}
            block
            options={[
              { label: 'Video', value: 'video', icon: <VideoCameraOutlined /> },
              { label: 'Audio', value: 'audio', icon: <CustomerServiceOutlined /> },
              { label: 'Thumbnail', value: 'thumbnail', icon: <PictureOutlined /> },
            ]}
          />
          <Space wrap>
            {mode === 'video' && (
              <>
                <Text strong>Video quality:</Text>
                <Select value={videoQuality} onChange={setVideoQuality} style={{ width: 220 }} options={VIDEO_QUALITY_OPTIONS} />
              </>
            )}
            {mode === 'audio' && (
              <>
                <Text strong>Audio format:</Text>
                <Select value={audioQuality} onChange={setAudioQuality} style={{ width: 220 }} options={AUDIO_QUALITY_OPTIONS} />
              </>
            )}
            {mode === 'thumbnail' && (
              <Text type="secondary">A cover thumbnail (JPG) will be downloaded — one per link.</Text>
            )}
            {fsApiSupported && (
              <Tooltip title="Choose a folder on your computer where files will be saved">
                <Button icon={<FolderOpenOutlined />} onClick={pickFolder}>
                  {dirName ? `Folder: ${dirName}` : 'Choose Save Folder'}
                </Button>
              </Tooltip>
            )}
            <Button type="primary" icon={<DownloadOutlined />} loading={running} onClick={startDownload}>
              {mode === 'video' ? 'Download Video' : mode === 'audio' ? 'Download Audio' : 'Download Thumbnail'} ({parseLinks().length})
            </Button>
          </Space>

          {/* Clear "where files save" banner so the user always knows the destination folder. */}
          <Alert
            type={fsApiSupported && dirName ? 'success' : 'info'}
            showIcon
            icon={<FolderOpenOutlined />}
            style={{ borderRadius: 8 }}
            message={
              fsApiSupported
                ? (dirName
                    ? <span>Files will be saved to the <b>{dirName}</b> folder.</span>
                    : <span>Files will currently go to your <b>Browser's Downloads</b> folder. To pick your own folder, click <b>"Choose Save Folder"</b> above.</span>)
                : <span>Files will be saved to your <b>Browser's Downloads</b> folder.</span>
            }
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Note: Browser security ki wajah se webpage khud folder *automatic* nahi khol sakta — par download hote hi file isi folder mein aa jaati hai. Folder kholne ke liye apne computer mein wo folder open kar lena.
              </Text>
            }
          />
        </Space>
      </Card>

      {total > 0 && (
        <Card style={{ borderRadius: 12 }}>
          <Space style={{ marginBottom: 12 }} wrap>
            <Text>Progress: {done}/{total} done{failed ? `, ${failed} failed` : ''}</Text>
            <Progress percent={total ? Math.round((done / total) * 100) : 0} style={{ width: 240 }} />
          </Space>
          <Table dataSource={rows} columns={columns} rowKey={(r) => r.url} pagination={false} size="small" />
        </Card>
      )}

      <Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
        Note: Ye feature server pe yt-dlp + ffmpeg use karta hai (sirf VPS pe chalega, shared hosting pe nahi).
      </Paragraph>
    </div>
  );
}
